// Author: Ahsan Saleem
/**
 * Top-level error boundary — an operator's screen must never go blank. If any
 * render throws, we catch it and show a calm, branded recovery surface (reload or
 * sign out) instead of a white page. This is the last line of defence around the
 * whole app; per-screen data errors are still handled by AsyncBoundary.
 */
import { Component, type ReactNode } from 'react';
import { auth } from '../api.js';

interface State { error: Error | null }

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State { return { error }; }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // Diagnostics to the console; a telemetry sink can hook in here later.
    console.error('BloomOS render error:', error, info?.componentStack);
  }

  private signOut = () => {
    try { auth.token = null; auth.siteId = null; auth.siteName = null; sessionStorage.removeItem('bloomos_booted'); } catch { /* ignore */ }
    location.assign('/login');
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="fatal" role="alert">
        <div className="fatal-card">
          <div className="fatal-mark" aria-hidden>✳</div>
          <h1 className="fatal-h">Something interrupted BloomOS</h1>
          <p className="fatal-p">This screen hit an unexpected error. Your recorded data is safe — reload to keep operating.</p>
          <div className="fatal-actions">
            <button className="fatal-btn primary" onClick={() => location.reload()}>Reload BloomOS</button>
            <button className="fatal-btn" onClick={this.signOut}>Sign out</button>
          </div>
          {import.meta.env.DEV && <pre className="fatal-trace">{String(this.state.error.stack ?? this.state.error)}</pre>}
        </div>
      </div>
    );
  }
}
